import { useEffect } from "react"

import { CountryEnum } from "@boundless/bl-api-client/customer/models"
import { CitizenshipEnumOptions } from "@boundless/bl-api-client/customer/models/CitizenshipEnumLabels"
import { CountryEnumOptions } from "@boundless/bl-api-client/customer/models/CountryEnumLabels"
import { StateEnumOptions } from "@boundless/bl-api-client/customer/models/StateEnumLabels"
import { TitleEnumOptions } from "@boundless/bl-api-client/customer/models/TitleEnumLabels"
import { useFormContext } from "react-hook-form"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { AutosaveStatus } from "@/components/form-elements/AutosaveStatus"
import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormDatePicker } from "@/components/form-elements/FormDatePicker"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { FormInputSSN } from "@/components/form-elements/FormInputSSN"
import { FormInputZipCode } from "@/components/form-elements/FormInputZipCode"
import { FormSection } from "@/components/form-elements/FormSection"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { FormSlider } from "@/components/form-elements/FormSlider"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { ssnFieldCountryLabels } from "@/lib/constants"
import type { AutosaveState } from "@/lib/forms/useAutosaveDraft"
import { cn, sortByLabel } from "@/lib/utils"

type OwnershipDetailsProps = {
  onBack: () => void
  onAdditionalOwnerChange: (checked: boolean) => void
  isAdditionalOwnerStep?: boolean
  hasMultipleOwners: boolean
  isCheckboxDisabled: boolean | undefined
  isSubmitting?: boolean
  maxOwnershipPercentage?: number
  autosaveStatus: AutosaveState
}

export function OwnershipDetailsForm({
  onAdditionalOwnerChange,
  onBack,
  hasMultipleOwners,
  isCheckboxDisabled,
  isAdditionalOwnerStep = false,
  isSubmitting = false,
  maxOwnershipPercentage = 100,
  autosaveStatus,
}: OwnershipDetailsProps) {
  const form = useFormContext()

  const country = form.watch("country") || CountryEnum.US
  const isForeignCitizen = form.watch("isForeignCitizen")
  const citizenship = form.watch("citizenship") || CountryEnum.US

  const citizenshipLabel =
    citizenship in ssnFieldCountryLabels
      ? ssnFieldCountryLabels[citizenship as keyof typeof ssnFieldCountryLabels]
      : undefined

  useEffect(() => {
    // When switching to a country that doesn't require an SSN-like number,
    // or the user ticks "I'm a foreign citizen", clear any errors on the SSN field
    if (!citizenshipLabel || isForeignCitizen) {
      form.clearErrors("ssn")
    }
  }, [citizenshipLabel, isForeignCitizen])

  const stateOptions = StateEnumOptions.filter(({ value }) =>
    value.startsWith(country),
  )

  return (
    <div className="flex w-full">
      <div className="relative w-full space-y-6 rounded-2xl bg-white p-5 shadow-sm sm:p-6 lg:p-8">
        <div className="mb-4 flex items-center lg:mb-8">
          <div className="bg-primary mr-3 h-6 w-1"></div>
          <h2 className="text-xl font-semibold">
            {isAdditionalOwnerStep ? "Additional" : ""} Ownership Details
          </h2>
          <div className="ml-auto">
            <AutosaveStatus status={autosaveStatus} />
          </div>
        </div>
        {!isAdditionalOwnerStep && (
          <div className="mb-6">
            <h3 className="text-lg font-medium">
              Do you have more than one majority owner?
            </h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-start gap-2">
                <div>
                  <Checkbox
                    id="hasMultipleOwners"
                    className="form-checkbox"
                    onCheckedChange={onAdditionalOwnerChange}
                    checked={hasMultipleOwners}
                    disabled={isCheckboxDisabled}
                  />
                </div>
                <Label
                  htmlFor="hasMultipleOwners"
                  className="text-black-800 flex flex-row items-start text-sm font-light"
                >
                  Add additional owner
                </Label>
              </div>
            </div>
          </div>
        )}

        <div className="@container/form space-y-6">
          <FormSection>
            <FormInput
              name="firstName"
              label="First Name"
              placeholder="First Name"
            />

            <FormInput
              name="lastName"
              label="Last Name"
              placeholder="Last Name"
            />
          </FormSection>

          <FormSection>
            <FormSelect
              name="title"
              options={sortByLabel(TitleEnumOptions)}
              label="Title"
              placeholder="Title"
            />
            <FormDatePicker
              name="dateOfBirth"
              label="Date of Birth"
              placeholder="Date of Birth"
              initialValue={form.getValues().dateOfBirth}
              maxDate={new Date()}
            />
          </FormSection>

          <div className="w-full space-y-2">
            <FormSlider
              name="ownershipPercentage"
              placeholder="Ownership %"
              type="number"
              className="border-input bg-background flex h-9 max-w-20 items-center rounded-md border px-3"
              max={maxOwnershipPercentage}
            />
          </div>

          <div className="mt-6">
            <div>
              <div className="grid grid-cols-1 gap-4 @md/form:grid-cols-2 @md/form:gap-6 @xl/form:grid-cols-3">
                <FormSelect
                  name="country"
                  label="Country"
                  options={CountryEnumOptions}
                  placeholder="Country"
                  onChange={() => {
                    form.setValue("state", "")
                  }}
                />

                <FormSelect
                  name="state"
                  label="State/Province"
                  options={stateOptions}
                  placeholder="State/Province"
                />
                <FormInput name="city" label="City" placeholder="City" />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 @md/form:grid-cols-2 @md/form:gap-6 @xl/form:grid-cols-3">
                <div className="col-span-1 md:col-span-2 @md/form:col-span-2">
                  <FormInput
                    name="address"
                    label="Street Address"
                    placeholder="Street Address"
                  />
                </div>
                <FormInputZipCode name="zipCode" countryField="country" />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="grid grid-cols-1 items-start gap-4 @md/form:grid-cols-2 @md/form:gap-6">
              {/* Citizenship and its foreign national checkbox share a cell so
                  the checkbox reads as tied to it. The cell spans the full row
                  when the SSN-like field shows, so SSN and Credit Score pair up
                  on the next row instead of leaving an empty column. */}
              <div
                className={cn("space-y-3", {
                  "@md/form:col-span-2": citizenshipLabel,
                })}
              >
                <FormSelect
                  name="citizenship"
                  label="Citizenship"
                  options={CitizenshipEnumOptions}
                  placeholder="Citizenship"
                />
                <FormCheckbox
                  name="isForeignCitizen"
                  label="I am a foreign national relative to my business's registered country"
                  className="form-checkbox"
                />
              </div>

              <div
                className={cn("transition-opacity", {
                  // Faded out if the user is a foreign citizen (field not
                  // applicable, but we have a meaningful label to show).
                  "opacity-50": isForeignCitizen,
                  // Hidden rather than unmounted when we have no citizenship
                  // label, so FormInputSSN's country-change effect still
                  // clears a stale value without leaving an empty grid cell.
                  hidden: !citizenshipLabel,
                })}
                inert={isForeignCitizen || !citizenshipLabel}
              >
                <FormInputSSN
                  name="ssn"
                  countryField="citizenship"
                  label={citizenshipLabel}
                  disabled={isForeignCitizen}
                />
              </div>

              <div>
                <FormInput
                  name="creditScore"
                  label="Credit Score"
                  placeholder="Credit Score"
                  type="number"
                />
              </div>

              <div>
                <FormInputPhone
                  name="phone"
                  label="Phone Number"
                  placeholder="Phone Number"
                />
              </div>

              <div>
                <FormInput name="email" label="Email" placeholder="Email" />
              </div>
            </div>
          </div>

          {form.formState.errors.root && (
            <p className="text-destructive text-sm font-medium">
              {form.formState.errors.root.message}
            </p>
          )}
          <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <ButtonBack onClick={onBack} className="w-full sm:w-auto" />
            <ButtonNext
              type="submit"
              className="w-full sm:w-auto"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
